import React, { useState } from 'react';
import { dashboardUri } from 'Consts/clientUris';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import useDarkHeaderTheme from 'Views/Onboarding/Hooks/useDarkHeaderTheme';
import { FDPrimaryActionButton, FDStack } from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useHistory } from 'react-router-dom';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import { WorkflowLayout } from 'Components/ComponentLibrary/Organisms/WorkflowLayout/WorkflowLayout';
import { SubSection } from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/CompletedOnboarding/SubSection';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import useMobileLayout from 'Hooks/useMobileLayout';
import { LottieLoadingLayout } from 'Components/ComponentLibrary/Organisms/LottieControl/LottieLoadingLayout';
import { dataAttrs } from 'Helpers/Testing';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import categories from 'EventTracking/consts/categories';
import eventTypes from 'EventTracking/consts/eventTypes';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import useHomeContext from 'Context/HomeContext';


const useStyles = buildUseStyles('CompletedOnboardingLayout', (_) => ({
  root: {
    animation: '$fade-in 1s',
  },
  '@keyframes fade-in': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

export default function CompletedOnboardingLayout() {
  const { resource, saving } = useMetaLayoutContext();
  const { refreshHomeResource } = useHomeContext();
  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const [sectionIdx, setSectionIdx] = useState(0);
  const { logStandardizedEvent } = useEventTrackingLogEvent();


  useScrollTopOnMount();
  useDarkHeaderTheme();
  useBackgroundColor(isMobile ? theme.palette.facet.lightGray : theme.palette.common.white);

  const { setHeaderVisibility } = useSurveyContext();
  React.useEffect(() => {
    setHeaderVisibility(false);
    refreshHomeResource();
  }, []);

  const sectionResources = resource.attr('subSections').resources().list();

  const isFirstStep = !sectionResources || sectionIdx === 0;
  let isLastStep = true;
  if (sectionResources) {
    isLastStep = sectionIdx >= sectionResources.length - 1;
  }
  let label = 'Next →';
  if (isLastStep) {
    label = 'Get started →';
  }

  return (
    <div className={classes.root}>
      <LottieLoadingLayout>
        <WorkflowLayout emphasized={isFirstStep} resource={resource}>
          <FDStack direction="column" justifyContent="center" alignItems="center" flex={1} spacing={2} overflow="hidden" width="100%">
            <MetaControlField attr="title" />
            <MetaControlField attr="body" />
            <SubSection resources={sectionResources} sectionIdx={sectionIdx} />
            <FDPrimaryActionButton
              loading={saving}
              label={label}
              {...dataAttrs('onboarding-continue')}
              onClick={async () => {
                logStandardizedEvent(categories.foundations, eventTypes.click, eventSubTypes.welcomeScreen, {
                  eventProperties: {
                    welcomeStepTitle: resource.attr('subSections').resources().index(sectionIdx).attr('type'),
                  },
                });
                if (!isLastStep) {
                  setSectionIdx(sectionIdx + 1);
                } else {
                  await resource.submit();
                  history.push(dashboardUri);
                }
              }}
            />
          </FDStack>
        </WorkflowLayout>
      </LottieLoadingLayout>
    </div>
  );
}
